// src/hooks/useDasboardMenu.ts

import { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { DashboardMenu } from "routes/DashboardRoutes";
import { DashboardMenuProps, ChildrenItemProps } from "types";
import { useDeviceMenuRefresh } from "context/DeviceMenuRefreshContext";
import { UserProfile } from "services/auth";
import http from "services/httpClient";

type Device = {
  id: number;
  device_name: string;
  client_id: number;
  client_name: string;
  client_user_ids: number[];
  client_role?: string;
};

type ClientFeatures = {
  id: number;
  client_id: number;
  sms_enabled: boolean;
  reports_enabled: boolean;
  updated_at: string;
  updated_by_user_id: number | null;
};

export function useDashboardMenu(user?: UserProfile | null) {
  const { refreshKey } = useDeviceMenuRefresh();
  const [menu, setMenu] = useState<DashboardMenuProps[]>(DashboardMenu);
  const [deviceGraphs, setDeviceGraphs] = useState<any[]>([]);

  const handleDeviceClick = async (deviceId: number, deviceName: string) => {
    try {
      const [latestRes, labelsRes, graphsRes, triggersRes] = await Promise.all([
        http.get(`/data/${deviceId}/data?range=latest`),
        http.get(`/device/${deviceId}/labels`),
        http.get(`/graphs/${deviceId}`),
        http.get(`/triggers/device/${deviceId}`),
      ]);

      const latest = await latestRes.data;
      const labels = await labelsRes.data;
      const graphs = await graphsRes.data;

      const triggers = (await triggersRes.data) as {
        column_index: number;
        threshold: number;
        operator: string;
      }[];

      const enrichedGraphs = graphs.map((graph: any) => {
        const config = graph.graph_config || {};
        const colIndex = config.label_column_index;

        const device_label =
          labels.find((l: any) => l.column_index === colIndex) || null;

        const key = colIndex !== undefined ? `d${colIndex}` : null;
        const value = key && latest ? latest[key] : null;
        const timestamp = latest?.timestamp || null;

        const trigger = triggers.find((t) => t.column_index === colIndex) || null;

        return {
          ...graph,
          graph_config: {
            ...config,
            device_label,
            value,
            timestamp,
            threshold: trigger?.threshold ?? null,
            operator: trigger?.operator ?? null,
          },
        };
      });

      setDeviceGraphs(enrichedGraphs);
    } catch (error) {
      console.error(`Error fetching data for ${deviceName}:`, error);
    }
  };

  useEffect(() => {
    if (!user) return;

    const fetchDevices = async () => {
      try {
        const response = await http.get(`/device?sort_by=client`);
        const devices: Device[] = response.data;

        const userId = user.id;
        const role = user.role;

        const filteredDevices = devices.filter((device) => {
          if (role === "owner" || role === "admin") return true;
          if (role === "client") return device.client_user_ids?.includes(userId);
          // if you also have global "manager" role:
          // if (role === "manager") return true;
          return false;
        });

        const grouped: Record<
          string,
          { client_id: number; client_name: string; devices: ChildrenItemProps[] }
        > = filteredDevices.reduce((acc, device) => {
          const clientId = device.client_id;
          const clientName = device.client_name || "Unknown Client";
          const key = `${clientId}::${clientName}`;

          if (!acc[key]) {
            acc[key] = { client_id: clientId, client_name: clientName, devices: [] };
          }

          acc[key].devices.push({
            id: uuid(),
            name: device.device_name,
            link: `/${device.device_name}`,
            onClick: () => handleDeviceClick(device.id, device.device_name),
          });

          return acc;
        }, {} as Record<string, { client_id: number; client_name: string; devices: ChildrenItemProps[] }>);

        // ---------- NEW: fetch client_features for each client_id ----------
        const clientIds = Array.from(new Set(Object.values(grouped).map((g) => g.client_id)));

        const clientRoleByClientId: Record<number, string | null> = {};
        const reportsEnabledByClientId: Record<number, boolean> = {};

        // Fetch client roles (only needed for non owner/admin)
        const needClientRoles = !(user.role === "owner" || user.role === "admin");

        const roleResults = needClientRoles
          ? await Promise.allSettled(
              clientIds.map(async (clientId) => {
                const res = await http.get(`/clients/${clientId}/role`);
                // supports "manager" OR { role: "manager" }
                const roleValue = typeof res.data === "string" ? res.data : (res.data?.role ?? null);
                return { clientId, role: roleValue as string | null };
              })
            )
          : [];

        if (!needClientRoles) {
          // owner/admin can be treated as "admin" for gating purposes
          for (const clientId of clientIds) clientRoleByClientId[clientId] = user.role;
        } else {
          for (const r of roleResults) {
            if (r.status === "fulfilled") {
              clientRoleByClientId[r.value.clientId] = r.value.role;
            } else {
              clientRoleByClientId[(r as any).value?.clientId] = null; // safe fallback
            }
          }
        }

        // Fetch client features (for ALL clients, because we need reports_enabled)
        const featureResults = await Promise.allSettled(
          clientIds.map(async (clientId) => {
            const res = await http.get(`/client_features/${clientId}`);
            return { clientId, data: res.data as ClientFeatures };
          })
        );

        for (const r of featureResults) {
          if (r.status === "fulfilled") {
            reportsEnabledByClientId[r.value.clientId] = !!r.value.data.reports_enabled;
          } else {
            reportsEnabledByClientId[(r as any).value?.clientId] = false;
          }
        }
        // ---------- END NEW ----------

        const clientItems: ChildrenItemProps[] = Object.values(grouped).map(
          ({ client_id, client_name, devices }) => {
            const clientRole = clientRoleByClientId[client_id]; // "manager" | "client" | "admin" | null
            const canViewReportsByRole =
              user.role === "owner" ||
              user.role === "admin" ||
              clientRole === "manager";

            const reportsEnabled = !!reportsEnabledByClientId[client_id];

            return {
              id: uuid(),
              title: client_name,
              link: "#",
              children: [
                ...(canViewReportsByRole && reportsEnabled
                  ? [
                      {
                        id: uuid(),
                        name: "Reports",
                        link: `/client/${client_id}/reports`,
                      },
                    ]
                  : []),
                ...devices,
              ],
            };
          }
        );

        const updatedMenu: DashboardMenuProps[] = DashboardMenu.map((item) =>
          item.title === "Dashboard" ? { ...item, children: clientItems } : item
        );

        setMenu(updatedMenu);
      } catch (error) {
        // console.error("Error fetching devices:", error);
      }
    };

    fetchDevices();
  }, [refreshKey, user]);

  return {
    menu,
    deviceGraphs,
    handleDeviceClick,
  };
}